import type { FC, ReactNode } from "react";
import { Pressable } from "react-native";
import { View, Text, type TextSize, type TextColorPalette } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";

/**
 * Width of each trailing checkbox column, shared by every row (data rows and
 * the site-group header) so their checkboxes land at identical x-positions
 * regardless of how long the row's label is.
 */
export const NOTIFICATION_CHECK_COLUMN_WIDTH = 46;
const ICON_COLUMN_WIDTH = 32;

export interface NotificationRowLayoutProps {
  /** Omit for rows with no meaningful per-row icon (e.g. every camera row would show the same decorative icon) — the label reclaims that space rather than leaving it blank. */
  readonly icon?: ReactNode;
  readonly label: string;
  readonly subtitle?: string;
  readonly labelTextStyle?: TextSize;
  /** Defaults to the theme's regular text color — pass `"textMuted"` to recede a row (e.g. a category row under a scope header). */
  readonly labelColor?: TextColorPalette | undefined;
  /** When provided, wraps the icon+label region (not the checkbox columns) in a Pressable — e.g. the site-group header's expand/collapse toggle. */
  readonly onPressLabel?: () => void;
  /** One fixed-width column per entry — 3 for a scope's Email/SMS/Push toggles. */
  readonly columns: readonly ReactNode[];
  readonly testID?: string;
}

/**
 * Shared row template: [icon][label — flexes, truncates][fixed-width
 * checkbox column]×N. On a screen too narrow to fit both, the checkbox
 * columns drop to their own line beneath the label (as a group, not one at a
 * time) rather than squeezing the label away.
 *
 * Used by `NotificationPreferenceGroup` (a scope's header — as a
 * press-to-collapse toggle — and its two category rows) and
 * `NotificationScopeListGroup`'s header (the "Sites"/"Cameras" section name),
 * so checkbox columns always align pixel-for-pixel between rows, independent
 * of label length.
 */
export const NotificationRowLayout: FC<NotificationRowLayoutProps> = ({
  icon,
  label,
  subtitle,
  labelTextStyle = "sm/regular",
  labelColor,
  onPressLabel,
  columns,
  testID,
}) => {
  const leftContent = (
    <>
      {icon !== undefined && <View style={styles.iconColumn}>{icon}</View>}
      <View style={styles.labelColumn}>
        <Text textStyle={labelTextStyle} numberOfLines={1} {...(labelColor !== undefined ? { color: labelColor } : {})}>
          {label}
        </Text>
        {subtitle !== undefined && (
          <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
            {subtitle}
          </Text>
        )}
      </View>
    </>
  );

  return (
    <View style={styles.row} testID={testID}>
      {onPressLabel !== undefined ?
        <Pressable onPress={onPressLabel} style={styles.left} accessibilityRole="button">
          {leftContent}
        </Pressable>
      : <View style={styles.left}>{leftContent}</View>}
      <View style={styles.columnsGroup}>
        {columns.map((column, index) => (
          <View key={index} style={styles.checkColumn}>
            {column}
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  row: {
    flexDirection: "row",
    // Lets the columns group (below) drop to its own line, as a whole,
    // instead of squeezing the label down to nothing on a narrow screen.
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing.sm,
    rowGap: theme.spacing.xs,
  },
  left: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
    // A floor so the label stops shrinking and the row wraps instead —
    // without it, flex:1 keeps shrinking the label arbitrarily thin rather
    // than ever handing the columns group a reason to move to the next line.
    minWidth: 120,
  },
  iconColumn: {
    width: ICON_COLUMN_WIDTH,
    alignItems: "center",
    justifyContent: "center",
  },
  labelColumn: {
    flex: 1,
    minWidth: 0,
  },
  columnsGroup: {
    flexDirection: "row",
    // Wraps too, as a fallback: normally this drops to its own line as one
    // whole unit (see `left`'s minWidth), but on a container narrower than
    // all the columns combined, this keeps individual columns cascading
    // onto further lines instead of overflowing off-screen. `flexShrink: 0`
    // alone isn't enough for that fallback — without `maxWidth`, this box
    // just grows to its full natural width and spills past the row's edge
    // rather than ever becoming small enough to need its own wrap.
    flexWrap: "wrap",
    maxWidth: "100%",
    gap: theme.spacing.sm,
    rowGap: theme.spacing.xs,
    flexShrink: 0,
  },
  checkColumn: {
    width: NOTIFICATION_CHECK_COLUMN_WIDTH,
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.xs,
  },
}));
